/**
 * Секция «Автоматизации пространства» (FR-12).
 *
 * Список правил, создание/редактирование, run-now, история.
 * Формат шагов:
 *  - items.update — itemType + filter (JSON) + actions (slug → значение)
 *  - jira.pull    — itemType + JQL + mode + runAsUser
 *  - jira.push    — view + changedSinceLastRun + runAsUser
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Drawer,
  Empty,
  Form,
  Input,
  InputNumber,
  message,
  Popconfirm,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
} from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  HistoryOutlined,
  PlayCircleOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import type {
  AutomationRule,
  AutomationRun,
  AutomationStepInput,
  CreateAutomationRuleRequest,
  ItemTypeListItem,
  SpaceMember,
  SpaceRole,
  View as ViewDto,
} from '@lens/shared';
import {
  createAutomationRule,
  deleteAutomationRule,
  listAutomationRules,
  listAutomationRuns,
  previewAutomationCron,
  runAutomationNow,
  updateAutomationRule,
} from './api.ts';
import { listItemTypes } from '../item-types/api.ts';
import { listViews } from '../views/api.ts';
import { listMembers } from '../spaces/api.ts';
import { RuleStepsEditor, type EditableStep } from './RuleStepsEditor.tsx';
import { CronScheduleBuilder } from './CronScheduleBuilder.tsx';

interface Props {
  spaceId: string;
  myRole?: SpaceRole;
  /** Включена ли Jira-интеграция — гейтит шаги Pull/Push в Jira. */
  jiraEnabled?: boolean;
}

function fmtDate(s: string | null): string {
  if (!s) return '—';
  return new Date(s).toLocaleString('ru-RU');
}

function statusTag(r: AutomationRule): JSX.Element {
  if (!r.enabled) return <Tag color="default">отключено</Tag>;
  if (r.lastError) return <Tag color="red">ошибка</Tag>;
  if (r.lastJobStatus === 'running' || r.lastJobStatus === 'pending')
    return <Tag color="processing">идёт</Tag>;
  if (r.lastJobStatus === 'success') return <Tag color="green">ок</Tag>;
  if (r.lastJobStatus === 'error') return <Tag color="red">упало</Tag>;
  return <Tag color="blue">включено</Tag>;
}

function runStatusTag(s: string): JSX.Element {
  switch (s) {
    case 'success':
      return <Tag color="green">success</Tag>;
    case 'failed':
      return <Tag color="red">failed</Tag>;
    case 'partial':
      return <Tag color="orange">partial</Tag>;
    case 'running':
      return <Tag color="processing">running</Tag>;
    case 'skipped':
      return <Tag color="default">skipped</Tag>;
    default:
      return <Tag>{s}</Tag>;
  }
}

export function AutomationRulesSection({ spaceId, myRole, jiraEnabled = false }: Props): JSX.Element {
  const isAdmin = myRole === 'admin';
  const [rules, setRules] = useState<AutomationRule[]>([]);
  const [loading, setLoading] = useState(false);

  // Reference data
  const [itemTypes, setItemTypes] = useState<ItemTypeListItem[]>([]);
  const [views, setViews] = useState<ViewDto[]>([]);
  const [members, setMembers] = useState<SpaceMember[]>([]);

  // Editor state
  const [editing, setEditing] = useState<AutomationRule | null>(null);
  const [creating, setCreating] = useState(false);
  const drawerOpen = creating || editing !== null;

  // History state
  const [historyRule, setHistoryRule] = useState<AutomationRule | null>(null);
  const [historyRuns, setHistoryRuns] = useState<AutomationRun[]>([]);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const items = await listAutomationRules(spaceId);
      setRules(items);
    } catch (e: any) {
      message.error(`Не удалось загрузить правила: ${e?.message ?? 'unknown'}`);
    } finally {
      setLoading(false);
    }
  }, [spaceId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    listItemTypes(spaceId).then(setItemTypes).catch(() => {});
    listViews(spaceId).then((res) => setViews(res.views.filter((v) => !v.isArchived))).catch(() => {});
    listMembers(spaceId).then((res) => setMembers(res.members)).catch(() => {});
  }, [spaceId]);

  const onRunNow = useCallback(
    async (rule: AutomationRule) => {
      try {
        await runAutomationNow(spaceId, rule.id);
        message.success('Запуск создан');
        await refresh();
      } catch (e: any) {
        message.error(`Не удалось запустить: ${e?.message ?? 'unknown'}`);
      }
    },
    [spaceId, refresh],
  );

  const onDelete = useCallback(
    async (ruleId: string) => {
      try {
        await deleteAutomationRule(spaceId, ruleId);
        message.success('Правило удалено');
        await refresh();
      } catch (e: any) {
        message.error(`Не удалось удалить: ${e?.message ?? 'unknown'}`);
      }
    },
    [spaceId, refresh],
  );

  const onToggleEnabled = useCallback(
    async (rule: AutomationRule, enabled: boolean) => {
      try {
        await updateAutomationRule(spaceId, rule.id, { enabled });
        await refresh();
      } catch (e: any) {
        message.error(`Не удалось обновить: ${e?.message ?? 'unknown'}`);
      }
    },
    [spaceId, refresh],
  );

  const openHistory = useCallback(
    async (rule: AutomationRule) => {
      setHistoryRule(rule);
      try {
        const runs = await listAutomationRuns(spaceId, rule.id);
        setHistoryRuns(runs);
      } catch (e: any) {
        message.error(`Не удалось загрузить историю: ${e?.message ?? 'unknown'}`);
      }
    },
    [spaceId],
  );

  const columns = useMemo(
    () => [
      {
        title: 'Название',
        dataIndex: 'name',
        key: 'name',
        render: (n: string, r: AutomationRule) => (
          <Space direction="vertical" size={0}>
            <Typography.Text strong>{n}</Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {r.steps.length} шаг(ов)
            </Typography.Text>
          </Space>
        ),
      },
      {
        title: 'Cron',
        dataIndex: 'cronExpression',
        key: 'cron',
        render: (c: string) => <Typography.Text code>{c}</Typography.Text>,
      },
      {
        title: 'Статус',
        key: 'status',
        render: (_: unknown, r: AutomationRule) => statusTag(r),
      },
      {
        title: 'Последний запуск',
        key: 'last',
        render: (_: unknown, r: AutomationRule) => (
          <Typography.Text type="secondary">{fmtDate(r.lastRunAt)}</Typography.Text>
        ),
      },
      {
        title: 'Следующий',
        key: 'next',
        render: (_: unknown, r: AutomationRule) => (
          <Typography.Text type="secondary">{fmtDate(r.nextRunAt)}</Typography.Text>
        ),
      },
      {
        title: 'Включено',
        key: 'enabled',
        render: (_: unknown, r: AutomationRule) => (
          <Switch
            checked={r.enabled}
            disabled={!isAdmin}
            onChange={(v) => onToggleEnabled(r, v)}
          />
        ),
      },
      {
        title: '',
        key: 'actions',
        render: (_: unknown, r: AutomationRule) => (
          <Space>
            <Button size="small" icon={<HistoryOutlined />} onClick={() => openHistory(r)}>
              История
            </Button>
            {isAdmin && (
              <>
                <Button
                  size="small"
                  icon={<PlayCircleOutlined />}
                  onClick={() => onRunNow(r)}
                >
                  Запустить
                </Button>
                <Button size="small" icon={<EditOutlined />} onClick={() => setEditing(r)}>
                  Изменить
                </Button>
                <Popconfirm
                  title="Удалить правило?"
                  okText="Удалить"
                  okButtonProps={{ danger: true }}
                  onConfirm={() => onDelete(r.id)}
                >
                  <Button size="small" icon={<DeleteOutlined />} danger>
                    Удалить
                  </Button>
                </Popconfirm>
              </>
            )}
          </Space>
        ),
      },
    ],
    [isAdmin, onRunNow, onDelete, onToggleEnabled, openHistory],
  );

  return (
    <Card
      title="Автоматизации"
      extra={
        isAdmin && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreating(true)}>
            Новое правило
          </Button>
        )
      }
    >
      {!isAdmin && (
        <Alert
          type="info"
          message="Создавать и редактировать правила могут только администраторы пространства."
          style={{ marginBottom: 16 }}
        />
      )}
      {rules.length === 0 && !loading ? (
        <Empty description="Правил пока нет" />
      ) : (
        <Table
          rowKey="id"
          dataSource={rules}
          columns={columns}
          pagination={false}
          loading={loading}
          size="small"
          expandable={{
            expandedRowRender: (r) => (
              <Space direction="vertical" style={{ width: '100%' }}>
                {r.lastError && (
                  <Alert type="error" message="Последняя ошибка" description={r.lastError} />
                )}
                <Typography.Text type="secondary">Лимит за прогон: {r.maxItemsPerRun}</Typography.Text>
                <ol style={{ paddingLeft: 20, margin: 0 }}>
                  {r.steps.map((s) => (
                    <li key={s.id}>
                      <Typography.Text code>{s.type}</Typography.Text>
                      {s.continueOnError && <Tag style={{ marginLeft: 8 }}>continue-on-error</Tag>}
                    </li>
                  ))}
                </ol>
              </Space>
            ),
          }}
        />
      )}

      <RuleEditorDrawer
        spaceId={spaceId}
        open={drawerOpen}
        rule={editing}
        itemTypes={itemTypes}
        views={views}
        members={members}
        jiraEnabled={jiraEnabled}
        onClose={() => {
          setEditing(null);
          setCreating(false);
        }}
        onSaved={async () => {
          setEditing(null);
          setCreating(false);
          await refresh();
        }}
      />

      <HistoryDrawer
        rule={historyRule}
        runs={historyRuns}
        onClose={() => {
          setHistoryRule(null);
          setHistoryRuns([]);
        }}
      />
    </Card>
  );
}

// ─── Editor Drawer ──────────────────────────────────────────────────────

interface EditorProps {
  spaceId: string;
  open: boolean;
  rule: AutomationRule | null;
  itemTypes: ItemTypeListItem[];
  views: ViewDto[];
  members: SpaceMember[];
  onClose: () => void;
  onSaved: () => Promise<void>;
  jiraEnabled?: boolean;
}

function RuleEditorDrawer({
  spaceId,
  open,
  rule,
  itemTypes,
  views,
  members,
  onClose,
  onSaved,
  jiraEnabled = false,
}: EditorProps): JSX.Element {
  const isEdit = rule !== null;
  const [form] = Form.useForm<{
    name: string;
    cronExpression: string;
    enabled: boolean;
    maxItemsPerRun: number;
  }>();
  const [steps, setSteps] = useState<EditableStep[]>([]);
  const [cronPreview, setCronPreview] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (rule) {
      form.setFieldsValue({
        name: rule.name,
        cronExpression: rule.cronExpression,
        enabled: rule.enabled,
        maxItemsPerRun: rule.maxItemsPerRun,
      });
      // Гидратируем шаги с дискриминатором.
      const ed: EditableStep[] = rule.steps.map((s) => ({
        type: s.type,
        config: s.config as Record<string, unknown>,
        continueOnError: s.continueOnError,
      })) as EditableStep[];
      setSteps(ed);
    } else {
      form.resetFields();
      form.setFieldsValue({
        name: '',
        cronExpression: '0 9 * * *',
        enabled: true,
        maxItemsPerRun: 1000,
      });
      setSteps([]);
    }
  }, [open, rule, form]);

  const watchedCron = Form.useWatch('cronExpression', form);

  useEffect(() => {
    if (!open) return;
    const expr = watchedCron;
    if (!expr || typeof expr !== 'string') {
      setCronPreview([]);
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const res = await previewAutomationCron(spaceId, expr, 3);
        if (!cancelled) setCronPreview(res.runs);
      } catch {
        if (!cancelled) setCronPreview([]);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [watchedCron, open, spaceId]);

  const onSave = useCallback(async () => {
    let values: { name: string; cronExpression: string; enabled: boolean; maxItemsPerRun: number };
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    if (steps.length === 0) {
      message.error('Добавьте хотя бы один шаг');
      return;
    }
    setSaving(true);
    try {
      const body: CreateAutomationRuleRequest = {
        ...values,
        steps: steps as AutomationStepInput[],
      };
      if (isEdit && rule) {
        await updateAutomationRule(spaceId, rule.id, body);
        message.success('Правило обновлено');
      } else {
        await createAutomationRule(spaceId, body);
        message.success('Правило создано');
      }
      await onSaved();
    } catch (e: any) {
      message.error(`Не удалось сохранить: ${e?.message ?? 'unknown'}`);
    } finally {
      setSaving(false);
    }
  }, [form, steps, isEdit, rule, spaceId, onSaved]);

  return (
    <Drawer
      title={isEdit ? `Правило «${rule?.name}»` : 'Новое правило автоматизации'}
      width={780}
      open={open}
      onClose={onClose}
      destroyOnClose
      extra={
        <Space>
          <Button onClick={onClose}>Отмена</Button>
          <Button type="primary" loading={saving} onClick={onSave}>
            Сохранить
          </Button>
        </Space>
      }
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="name"
          label="Название"
          rules={[{ required: true, max: 120 }]}
        >
          <Input placeholder="Например: Закрыть просроченные задачи" />
        </Form.Item>

        <Form.Item
          name="cronExpression"
          label="Расписание (МСК)"
          rules={[{ required: true }]}
          trigger="onChange"
        >
          <CronScheduleBuilder />
        </Form.Item>
        {cronPreview.length > 0 && (
          <Alert
            type="info"
            message="Следующие запуски:"
            description={cronPreview
              .map((d) =>
                new Date(d).toLocaleString('ru-RU', { timeZone: 'Europe/Moscow' }),
              )
              .join('  •  ') + ' (МСК)'}
            style={{ marginBottom: 16 }}
          />
        )}

        <Space size="large" style={{ marginBottom: 16 }}>
          <Form.Item name="enabled" label="Включено" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item
            name="maxItemsPerRun"
            label="Лимит записей за прогон"
            rules={[{ required: true, type: 'number', min: 1, max: 100000 }]}
          >
            <InputNumber min={1} max={100000} step={100} />
          </Form.Item>
        </Space>
      </Form>

      <Typography.Title level={5} style={{ marginTop: 12 }}>
        Шаги (выполняются последовательно)
      </Typography.Title>
      <Typography.Paragraph type="secondary">
        По умолчанию ошибка шага останавливает всю цепочку. Включите «продолжать при
        ошибке», если шаг не критичен.
      </Typography.Paragraph>
      <RuleStepsEditor
        spaceId={spaceId}
        steps={steps}
        onChange={setSteps}
        itemTypes={itemTypes}
        views={views}
        members={members}
        jiraEnabled={jiraEnabled}
      />
    </Drawer>
  );
}

// ─── History Drawer ─────────────────────────────────────────────────────

interface HistoryProps {
  rule: AutomationRule | null;
  runs: AutomationRun[];
  onClose: () => void;
}

function HistoryDrawer({ rule, runs, onClose }: HistoryProps): JSX.Element {
  return (
    <Drawer
      title={rule ? `История запусков · ${rule.name}` : 'История запусков'}
      width={720}
      open={rule !== null}
      onClose={onClose}
      destroyOnClose
    >
      {runs.length === 0 ? (
        <Empty description="Запусков пока не было" />
      ) : (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          {runs.map((r) => (
            <Card
              key={r.id}
              size="small"
              title={
                <Space>
                  {runStatusTag(r.status)}
                  <Typography.Text type="secondary">
                    {fmtDate(r.startedAt)} → {fmtDate(r.finishedAt)}
                  </Typography.Text>
                  {r.triggeredBy === 'manual' && <Tag>вручную</Tag>}
                </Space>
              }
            >
              {r.error && (
                <Alert type="error" message={r.error} style={{ marginBottom: 8 }} />
              )}
              <Table
                size="small"
                pagination={false}
                rowKey="id"
                dataSource={r.steps}
                columns={[
                  { title: '#', dataIndex: 'order', key: 'order', width: 40 },
                  {
                    title: 'Шаг',
                    dataIndex: 'type',
                    key: 'type',
                    render: (t: string) => <Typography.Text code>{t}</Typography.Text>,
                  },
                  {
                    title: 'Статус',
                    dataIndex: 'status',
                    key: 'status',
                    render: runStatusTag,
                  },
                  {
                    title: 'Найдено',
                    dataIndex: 'matchedCount',
                    key: 'matched',
                  },
                  {
                    title: 'Применено',
                    dataIndex: 'updatedCount',
                    key: 'updated',
                  },
                  {
                    title: 'Ошибка',
                    dataIndex: 'error',
                    key: 'error',
                    ellipsis: true,
                    render: (e: string | null) => e ?? '—',
                  },
                ]}
              />
            </Card>
          ))}
        </Space>
      )}
    </Drawer>
  );
}
